import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { ColumnAttribute } from '@/components/content/HdmSkucomparer/parts/ColumnAttribute';
import { ColumnProduct } from '@/components/content/HdmSkucomparer/parts/ColumnProduct';
import { ColumnText } from '@/components/content/HdmSkucomparer/parts/ColumnText';
import { ColumnTitle } from '@/components/content/HdmSkucomparer/parts/ColumnTitle';
import { ComparingWithControls } from '@/components/content/HdmSkucomparer/parts/ComparingWithControls';
import { Row } from '@/components/content/HdmSkucomparer/parts/Row';
import { hdmSkucomparerStyles } from '@/components/content/HdmSkucomparer/styles';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableSection,
} from '@/components/content/ProductDetails/hooks/useNavigableSection';
import { ASSOCIATION_TYPES, TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { WidgetComponentProps } from '@/data/types/Slot';
import { productIsA } from '@/utils/productIsA';
import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { mapProductData } from '@/data/utils/mapProductData';

const MAX_ASSOCIATIONS = 3;
const PDP_COMPARE_SUPERRIOR_SECTION_ESPOT = 'compareProducts_SuperiorSection';

export const HdmSkucomparer: FC<WidgetComponentProps> = ({ id }) => {
	const navigableSectionProps = useNavigableSection({
		sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_COMPARE,
	});

	const localization = useLocalization('HDMSkucomparer');

	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
	const showLargeTitle = useMediaQuery(theme.breakpoints.up('md'));

	const partNumber = id.toString();
	const { selection, product, isBuyable } = useProductDetails({
		partNumber,
	});

	const isBundle = productIsA(product, TYPES.bundle);
	const sku = !isBundle ? selection?.sku : product;

	const associations = useMemo(
		() =>
			(sku?.merchandisingAssociations ?? [])
				.filter((e) => e.associationType === ASSOCIATION_TYPES.compare)
				.map((e) => mapProductData(e))
				.filter((assoc) => assoc.productPrice?.offer !== 0)
				.slice(0, MAX_ASSOCIATIONS),
		[sku?.merchandisingAssociations]
	);
	const attributeNames = useMemo(() => {
		if (!associations.length) return [];

		const association = associations.find((a) => a.attList);
		if (!association) return [];

		return association.attList?.split('|') ?? [];
	}, [associations]);

	const [comparingWithIndex, setComparingWithIndex] = useState(0);
	const onNext = () => setComparingWithIndex((i) => (i + 1) % associations.length);
	const onPrevious = () =>
		setComparingWithIndex((i) =>
			i === 0 ? associations.length - 1 : (i - 1) % associations.length
		);

	if (!sku || !associations.length || !isBuyable) {
		return null;
	}

	return (
		<Paper sx={hdmSkucomparerStyles.container} {...navigableSectionProps}>
			<Box sx={hdmSkucomparerStyles.header}>
				<Typography component="h3" variant="headingSegment">
					{showLargeTitle ? localization.CompareSimilarProducts.t() : localization.Compare.t()}
				</Typography>

				{isDesktop ? null : (
					<ComparingWithControls
						totalAssociations={associations.length}
						comparingWithIndex={comparingWithIndex}
						onNext={onNext}
						onPrevious={onPrevious}
					/>
				)}
			</Box>
			<EMarketingSpot
				id={PDP_COMPARE_SUPERRIOR_SECTION_ESPOT}
				properties={{
					emsName: PDP_COMPARE_SUPERRIOR_SECTION_ESPOT,
				}}
			/>
			<Box sx={hdmSkucomparerStyles.table}>
				{/* Product */}
				<Row>
					<ColumnTitle title={localization.Product.t()} />

					<ColumnProduct product={sku} isMainProduct />

					{associations.map((association, index) => (
						<ColumnProduct
							key={association.id}
							product={association}
							isVisible={isDesktop ? true : index === comparingWithIndex}
						/>
					))}
				</Row>

				{/* Brand */}
				<Row>
					<ColumnTitle title={localization.Brand.t()} />

					<ColumnText name={localization.Brand.t()} text={sku.manufacturer} isMainProduct />

					{associations.map((association, index) => (
						<ColumnText
							key={association.id}
							name={localization.Brand.t()}
							text={association.manufacturer}
							isVisible={isDesktop ? true : index === comparingWithIndex}
						/>
					))}
				</Row>

				{/* Attributes */}
				{attributeNames.map((attributeName) => (
					<Row key={attributeName}>
						<ColumnTitle title={attributeName} />

						<ColumnAttribute attributes={sku.attributes} name={attributeName} isMainProduct />

						{associations.map((association, index) => (
							<ColumnAttribute
								key={association.id}
								attributes={association.attributes}
								name={attributeName}
								isVisible={isDesktop ? true : index === comparingWithIndex}
							/>
						))}
					</Row>
				))}
			</Box>
		</Paper>
	);
};
